import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { getFirebaseToken } from '../utils/getFirebaseToken';
import { getViewerTimezone } from '../utils/dateTime';
import './CmaReportPage.css';
import CmaReviewModal from '../components/transaction/CmaReviewModal';

const formatCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
};

const formatDate = (isoish) => {
  if (!isoish) return '';
  try {
    const d = new Date(isoish);
    if (Number.isNaN(d.getTime())) return isoish;
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      timeZone: getViewerTimezone(),
    });
  } catch {
    return isoish;
  }
};

const TREND_LABEL = {
  APPRECIATING: 'Appreciating',
  STABLE: 'Stable',
  DEPRECIATING: 'Depreciating',
};

const BackIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

const CmaReportPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpeningPdf, setIsOpeningPdf] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const fetchReport = useCallback(async () => {
    if (!reportId) return;
    setIsLoading(true);
    setError(null);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.cmaReportById(reportId), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReport(res.data);
    } catch (err) {
      console.error('[CmaReportPage] fetch failed', err);
      setError(
        err?.response?.status === 403
          ? "You don't have access to this CMA report."
          : err?.response?.status === 404
            ? 'This CMA report could not be found.'
            : 'Could not load this CMA report. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  // Same "open a trusted tab synchronously, navigate it once the blob is ready"
  // pattern as TransactionDocuments.js's document viewer — keeps popup blockers
  // from intervening since the window.open() call happens inside the click handler.
  const handleViewPdf = async () => {
    const newTab = window.open('', '_blank');
    if (newTab) {
      newTab.document.write(
        '<!DOCTYPE html><html><head><title>Opening CMA report…</title></head>' +
        '<body style="font-family:sans-serif;color:#2D1B69;display:flex;align-items:center;justify-content:center;height:100vh;margin:0">' +
        '<p>Opening CMA report…</p></body></html>'
      );
      newTab.document.close();
    }
    setIsOpeningPdf(true);
    try {
      const token = await getFirebaseToken();
      const res = await apiClient.get(API_ENDPOINTS.cmaReportPdf(reportId), {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = blobUrl;
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('[CmaReportPage] PDF open failed', err);
      if (newTab) newTab.close();
      alert('Could not open the PDF. Please try again.');
    } finally {
      setIsOpeningPdf(false);
    }
  };

  if (isLoading) {
    return (
      <div className="cma-page">
        <div className="cma-page-header">
          <button type="button" className="cma-back-btn" onClick={() => navigate(-1)}><BackIcon /></button>
          <h1>CMA Report</h1>
        </div>
        <div className="cma-loading">Loading report…</div>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="cma-page">
        <div className="cma-page-header">
          <button type="button" className="cma-back-btn" onClick={() => navigate(-1)}><BackIcon /></button>
          <h1>CMA Report</h1>
        </div>
        <div className="cma-error">{error || 'This CMA report could not be found.'}</div>
      </div>
    );
  }

  const subject = report.subjectProperty || {};
  const pricing = report.pricingRecommendation || {};
  const comps = report.selectedComps || [];
  const approaches = report.approaches || [];
  const strategies = report.pricingStrategies || [];

  return (
    <div className="cma-page">
      <div className="cma-page-header">
        <button type="button" className="cma-back-btn" onClick={() => navigate(-1)}><BackIcon /></button>
        <div>
          <h1>CMA Report</h1>
          <p className="cma-subtitle">{report.requestedAddress}{report.version ? ` · Version ${report.version}` : ''}</p>
        </div>
        <button type="button" className="cma-btn-secondary cma-header-action" onClick={handleViewPdf} disabled={isOpeningPdf}>
          {isOpeningPdf ? 'Opening…' : 'View PDF'}
        </button>
        {report.draftId && (
          <button type="button" className="cma-btn-secondary cma-header-action" onClick={() => setReviewOpen(true)}>
            Edit comps &amp; regenerate
          </button>
        )}
      </div>

      <div className="cma-hero">
        <div className="cma-hero-price">
          <span className="cma-hero-label">Suggested List Price</span>
          <span className="cma-hero-value">{formatCurrency(pricing.suggestedListPrice)}</span>
          <span className="cma-hero-range">{formatCurrency(pricing.lowEstimate)} – {formatCurrency(pricing.highEstimate)}</span>
        </div>
        <div className="cma-hero-badges">
          {pricing.confidenceLevel && <span className="cma-badge cma-badge-confidence">{pricing.confidenceLevel} confidence</span>}
          {pricing.marketCondition && <span className="cma-badge">{pricing.marketCondition.replace(/_/g, ' ')}</span>}
          {pricing.marketTrendDirection && (
            <span className={`cma-badge cma-badge-trend-${pricing.marketTrendDirection.toLowerCase()}`}>
              {TREND_LABEL[pricing.marketTrendDirection] || pricing.marketTrendDirection}
              {pricing.annualizedAppreciationRate != null ? ` (${pricing.annualizedAppreciationRate}%/yr)` : ''}
            </span>
          )}
        </div>
        {pricing.rationale && <p className="cma-rationale">{pricing.rationale}</p>}
      </div>

      <div className="cma-section">
        <h2>Subject Property</h2>
        <div className="cma-facts-row">
          {subject.bedrooms != null && <div className="cma-fact"><span>{subject.bedrooms}</span>Beds</div>}
          {subject.bathrooms != null && <div className="cma-fact"><span>{subject.bathrooms}</span>Baths</div>}
          {subject.squareFeet != null && <div className="cma-fact"><span>{subject.squareFeet.toLocaleString()}</span>Sq Ft</div>}
          {subject.yearBuilt != null && <div className="cma-fact"><span>{subject.yearBuilt}</span>Year Built</div>}
          {subject.propertyType && <div className="cma-fact"><span>{subject.propertyType}</span>Type</div>}
        </div>
      </div>

      {strategies.length > 0 && (
        <div className="cma-section">
          <h2>Pricing Strategies</h2>
          <div className="cma-card-grid">
            {strategies.map((s, i) => (
              <div className="cma-strategy-card" key={i}>
                <h3>{s.strategyName}</h3>
                <div className="cma-strategy-price">{formatCurrency(s.listPrice)}</div>
                {s.priceVsEstimate && <div className="cma-strategy-meta">{s.priceVsEstimate}</div>}
                {s.targetDaysOnMarket && <div className="cma-strategy-meta">Est. {s.targetDaysOnMarket} on market</div>}
                {s.idealFor && <p className="cma-strategy-ideal">{s.idealFor}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {approaches.length > 0 && (
        <div className="cma-section">
          <h2>Valuation Approaches</h2>
          <div className="cma-card-grid">
            {approaches.map((a, i) => (
              <div className="cma-approach-card" key={i}>
                <h3>{a.approachName}</h3>
                <div className="cma-approach-value">{formatCurrency(a.estimatedValue)}</div>
                <div className="cma-strategy-meta">{formatCurrency(a.lowEstimate)} – {formatCurrency(a.highEstimate)}</div>
                {a.confidenceLevel && <div className="cma-strategy-meta">{a.confidenceLevel} confidence · {a.dataPointsUsed || 0} comps</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {comps.length > 0 && (
        <div className="cma-section">
          <h2>Comparable Sales ({comps.length})</h2>
          <div className="cma-table-wrap">
            <table className="cma-comps-table">
              <thead>
                <tr>
                  <th>Address</th>
                  <th>Sale Price</th>
                  <th>Sale Date</th>
                  <th>Beds / Baths</th>
                  <th>Sq Ft</th>
                  <th>Distance</th>
                </tr>
              </thead>
              <tbody>
                {comps.map((c, i) => (
                  <tr key={i}>
                    <td>{c.address}{c.city ? `, ${c.city}` : ''}{c.source === 'MANUAL' && <span className="cma-badge cma-badge-agent">Agent-provided</span>}</td>
                    <td>{formatCurrency(c.salePrice)}</td>
                    <td>{formatDate(c.saleDate)}</td>
                    <td>{c.bedrooms ?? '—'} / {c.bathrooms ?? '—'}</td>
                    <td>{c.squareFeet != null ? c.squareFeet.toLocaleString() : '—'}</td>
                    <td>{c.distanceMiles != null ? `${c.distanceMiles.toFixed(1)} mi` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {(report.marketSummary || report.marketTrendSummary) && (
        <div className="cma-section">
          <h2>Market Summary</h2>
          {report.marketSummary && <p>{report.marketSummary}</p>}
          {report.marketTrendSummary && <p>{report.marketTrendSummary}</p>}
        </div>
      )}

      <div className="cma-footer">
        {report.dataQualityNote && <p className="cma-footer-note">{report.dataQualityNote}</p>}
        {report.disclaimer && <p className="cma-disclaimer">{report.disclaimer}</p>}
        {report.reportDate && <p className="cma-footer-note">Generated {formatDate(report.reportDate)} · Data source: {report.dataSource || 'ATTOM'}</p>}
      </div>

      {reviewOpen && (
        <CmaReviewModal
          transaction={{ id: report.transactionId }}
          address={report.requestedAddress}
          draftId={report.draftId}
          onClose={() => setReviewOpen(false)}
          onGenerated={(next) => navigate(`/cma/${next.reportId}`)}
        />
      )}
    </div>
  );
};

export default CmaReportPage;
